import { useEffect, useState, useMemo } from 'react';
import styles from './style.module.scss';
import { List, Divider, Row, Col, Spin, Select, message, Typography } from 'antd';
import { Button, PageHeader } from 'components';
import { Link, Routes, Route } from 'react-router-dom';
import DeleteModal from 'components/deleteModalView';
import { useTranslation } from 'react-i18next';
import UIHelper from 'helpers/UIHelper';
import useExtraSales from '../useExtraSales';
import FiltersApi from 'libs/api/filters';
import { getExtraSaleViewSections } from '../extraSaleViewSections';

const apiFilters = new FiltersApi();

const ViewSection = ({ title, fields }) => (
  <List
    header={<div>{title}</div>}
    style={{ marginTop: 24 }}
    dataSource={fields}
    renderItem={(item) => (
      <List.Item>
        <b className={styles.itemName}>{item.text}</b>
        {item.value}
      </List.Item>
    )}
  />
);

const SingleExtraSale = () => {
  const { t } = useTranslation();
  const {
    singleItem,
    loading,
    handleMemberApprove,
    handleCheckMemberByCompany,
    shoppingCenters,
    companyCategories,
  } = useExtraSales(false);

  const companyCategoriesIds = useMemo(
    () => singleItem?.allData?.company_categories?.map((c) => c.id) || [],
    [singleItem],
  );

  const [allCompaniesByCompanyCategory, setAllCompaniesByCompanyCategory] =
    useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (companyCategoriesIds.length > 0) {
          const { data } = await apiFilters.list({
            params: {
              by_company_categories: companyCategoriesIds,
            },
          });
          const list = data.all_companies_by_company_category || [];
          if (!cancelled) setAllCompaniesByCompanyCategory(list);
        }
      } catch (error) {
        if (!cancelled) console.error(error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyCategoriesIds]);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedExtrasaleHourId, setSelectedExtrasaleHourId] = useState(null);
  const [disableApproveRejectBt, setDisableApproveRejectBt] = useState(false);
  const [commentTextBefore, setCommentTextBefore] = useState('');

  const extrasaleHours = singleItem?.allData?.extrasale_hours || [];

  const sections = useMemo(
    () =>
      getExtraSaleViewSections(t, singleItem, {
        shoppingCenters,
        companyCategories,
      }),
    [t, singleItem, shoppingCenters, companyCategories],
  );

  const validateForm = () => {
    if (!selectedCompany) {
      message.error(t("L'insegna deve essere selezionata"));
      return false;
    }
    if (!selectedExtrasaleHourId && extrasaleHours.length > 0) {
      message.error(t('Fascia oraria deve essere selezionata'));
      return false;
    }
    return true;
  };

  const handleApprove = async () => {
    if (!validateForm()) return;
    await handleMemberApprove({
      company_id: selectedCompany,
      extrasale_hour_id: selectedExtrasaleHourId,
      comment: '',
      confirmed: true,
    });
    setSelectedCompany(null);
    setSelectedExtrasaleHourId(null);
  };

  const handleReject = async () => {
    if (!validateForm()) return;
    await handleMemberApprove({
      company_id: selectedCompany,
      extrasale_hour_id: selectedExtrasaleHourId,
      comment: '',
      confirmed: false,
    });
    setSelectedCompany(null);
    setSelectedExtrasaleHourId(null);
  };

  useEffect(() => {
    if (!selectedCompany || !selectedExtrasaleHourId) return;
    handleCheckMemberByCompany(selectedCompany, selectedExtrasaleHourId)
      .then((response) => {
        if (!response?.member) return;
        setCommentTextBefore(response.member.comment ?? '');
        UIHelper.openNotificationWithIcon(
          'error',
          response.member.confirmed
            ? t('Attività già approvata')
            : t('Attività non approvata'),
        );
        setDisableApproveRejectBt(true);
      })
      .catch(() => {
        setCommentTextBefore('');
        setDisableApproveRejectBt(false);
      });
  }, [selectedCompany, selectedExtrasaleHourId, handleCheckMemberByCompany, t]);

  const pageTitle = singleItem?.title || t('ATTIVITÀ');

  return singleItem ? (
    <div>
      <PageHeader title={pageTitle} />
      <div className={styles.content}>
        <Spin spinning={loading}>
          {sections.map((section) => (
            <ViewSection
              key={section.title}
              title={section.title}
              fields={section.fields}
            />
          ))}
        </Spin>

        {companyCategoriesIds.length > 0 &&
          allCompaniesByCompanyCategory.length > 0 && (
            <div className={styles.approveForm}>
              <Divider />
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Select
                    placeholder={t('Insegna')}
                    style={{ width: '100%' }}
                    value={selectedCompany}
                    onSelect={(value) => {
                      setSelectedCompany(value);
                      setDisableApproveRejectBt(false);
                      setCommentTextBefore('');
                    }}
                  >
                    <Select.Option value={''}>
                      {t('Selezioni opzione')}
                    </Select.Option>
                    {allCompaniesByCompanyCategory.map((company) => (
                      <Select.Option key={company.value} value={company.value}>
                        {company.title}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
                {extrasaleHours.length > 0 && (
                  <Col span={12}>
                    <Select
                      placeholder={t('Fascia oraria')}
                      style={{ width: '100%' }}
                      value={selectedExtrasaleHourId ?? undefined}
                      onSelect={(value) => setSelectedExtrasaleHourId(value)}
                    >
                      <Select.Option value={''}>
                        {t('Selezioni opzione')}
                      </Select.Option>
                      {extrasaleHours.map((hour) => (
                        <Select.Option key={hour.id} value={hour.id}>
                          {hour.date_title}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>
                )}
                {commentTextBefore && (
                  <Col span={24}>
                    <Typography.Text strong>{t('Commento')}: </Typography.Text>
                    <Typography.Text>{commentTextBefore}</Typography.Text>
                  </Col>
                )}
                <Col span={24}>
                  <Row gutter={[16, 16]}>
                    <Col>
                      <Button
                        type='primary'
                        disabled={disableApproveRejectBt}
                        onClick={handleApprove}
                      >
                        {t('Partecipo')}
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        type='primary'
                        danger
                        disabled={disableApproveRejectBt}
                        onClick={handleReject}
                      >
                        {t('Non partecipo')}
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>
          )}

        <Divider />
        <Row gutter={16}>
          {singleItem?.can_update && (
            <Col>
              <Button>
                <Link to={`/promo/extrasales/${singleItem?.id}/edit`}>
                  {t('MODIFICA')}
                </Link>
              </Button>
            </Col>
          )}
          <Col>
            <Button type={'secondary'}>
              <Link to={'/promo/extrasales'}>{t('TORNA INDIETRO')}</Link>
            </Button>
          </Col>
        </Row>
      </div>
      <Routes>
        <Route
          path={':shopId/delete'}
          element={
            <DeleteModal pathname={`/promo/extrasales/${singleItem.id}`} />
          }
        />
      </Routes>
    </div>
  ) : (
    <Spin size={'large'} />
  );
};

export default SingleExtraSale;
